import { BitkitAlert, BitkitEmptyState, BitkitSpinner, IconReact } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useContext } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';

import useChildInvocations from './hooks/useChildInvocations';
import InvocationGroupTable from './InvocationGroupTable';

type ChildInvocationsSectionProps = {
  invocationId: string;
  enabled?: boolean;
  showTitle?: boolean;
};

const ChildInvocationsSection = ({ invocationId, enabled, showTitle = true }: ChildInvocationsSectionProps) => {
  const {
    account: { slug: workspaceSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const {
    data,
    isError,
    isLoading,
    refetch: refetchChildInvocations,
  } = useChildInvocations(workspaceSlug, invocationId, enabled);

  return (
    <Box display="flex" flexDir="column" gap="1rem">
      {showTitle && <Text textStyle="heading/h3">Child invocations</Text>}

      {isError && (
        <BitkitAlert
          variant="critical"
          action={{ label: 'Retry', onClick: refetchChildInvocations }}
          titleText="Failed to load invocations"
          messageText="Check your connection and try again."
        />
      )}

      {isLoading && (
        <Box
          alignItems="center"
          bg="background/secondary"
          border="1px solid"
          borderColor="border/minimal"
          borderRadius="4"
          display="flex"
          flexDir="column"
          gap="8"
          padding="48"
        >
          <BitkitSpinner width="24" height="24" aria-hidden />
          <Text textStyle="body/lg/semibold" color="text/secondary">
            Loading invocations...
          </Text>
          <Text textStyle="body/md/regular" color="text/secondary">
            Child invocations will appear here.
          </Text>
        </Box>
      )}

      {!isLoading && data?.length === 0 && (
        <BitkitEmptyState
          icon={IconReact}
          headingText="Child invocations will appear here"
          bodyText="No invocations are available."
        />
      )}

      {data?.map((group) => (
        <InvocationGroupTable
          key={group.buildTool}
          group={group}
          workspaceSlug={workspaceSlug}
          testIdPrefix="childInvocationsTable"
        />
      ))}
    </Box>
  );
};

export default ChildInvocationsSection;
